"use client";

import * as React from "react";
import { cn } from "@baluu/ui/lib/utils";
import { Button } from "@baluu/ui/components/button";
import { TicketSelectorV2 } from "./ticket-selector-v2";
import { CheckoutSteps } from "@baluu/ui/components/checkout/checkout-steps";
import { BundleMembershipHelpPopup } from "@baluu/ui/components/checkout/bundle-membership-help-popup";
import { Info, Tag } from "lucide-react";
import type { TicketBookingPolicy } from "./utils/types";
import type { ValidationError } from "../../lib/pricing";
import type { CheckoutTicketSummary } from "./ticket-conditions-chips";
import { capSeatsByPool,
  poolCapAcrossTicketTypes,
} from "../../lib/checkout/pool-availability";
import { ruleDiscountHintLabel, ruleEvaluatedConditions } from "./utils/rule-conditions";

type V2TicketType = {
  id: string;
  name: string;
  description?: string;
  price: number;
  maxQuantity: number;
  minQuantity: number;
  soldOut?: boolean;
  numberOfGuestsPerTicket?: number;
  maxLimit?: number;
  currentSold?: number;
  isInTimeConstraint?: boolean;
  typeCharge?: "flat" | "perPerson" | "perPersonPerLesson" | "flatPerPerson";
  ticketTypeGroup?: "regular" | "deposit";
  depositAmount?: number;
  effectivePrice?: number;
  effectiveDepositAmount?: number;
  chargeType?: string;
  chargeConfig?: any;
  membershipRequired?: boolean;
  membershipAccessGranted?: boolean;
  eligibilityMessage?: string;
  policyHint?: string;
  bookingPolicy?: TicketBookingPolicy | null;
  pricingRules?: any[];
  checkoutSummary?: CheckoutTicketSummary | null;
  /** Both already sent by the course endpoint; needed to share one pool's cap
   *  across every ticket type drawing on it. */
  resourcePoolId?: string | null;
  resourceUnitsPerTicket?: number | null;
};

type TicketSelection = {
  ticketTypeId: string;
  quantity: number;
};

type TicketSelectionStepV2Props = {
  ticketTypes: V2TicketType[];
  selections: TicketSelection[];
  onSelectionChange: (ticketTypeId: string, quantity: number) => void;
  currency?: string;
  totalAvailability?: number;
  className?: string;
  hidePrice?: boolean;
  bundleMaxTickets?: number;
  isMultiDateEnabled?: boolean;
  dateCount?: number;
  membershipsUrl?: string;
  constraintErrors?: ValidationError[];
  cartPricingRules?: any[];
  /** @deprecated Use ticketType.pricingRules instead */
  pricingRules?: any[];
  fallbackBookingPolicy?: TicketBookingPolicy | null;
  onPolicyClick?: (
    ticketName: string,
    policy: TicketBookingPolicy,
  ) => void;
  showBundleMembershipHelp?: boolean;
  /**
   * Equipment cap per ticket type for the SELECTED session/date, from the
   * pool-availability read. Absent key (or absent map) = uncapped — the
   * fail-open rule: a pool constraint that failed to load degrades to
   * today's behaviour, never to 0.
   */
  poolMaxTicketsByTicketTypeId?: Record<string, number>;
};

function TicketSelectionStepV2({
  ticketTypes,
  selections,
  onSelectionChange,
  currency = "GBP",
  totalAvailability,
  className,
  hidePrice = false,
  bundleMaxTickets,
  isMultiDateEnabled = false,
  dateCount = 1,
  membershipsUrl,
  constraintErrors = [],
  cartPricingRules,
  pricingRules: _deprecatedPricingRules,
  fallbackBookingPolicy,
  onPolicyClick,
  showBundleMembershipHelp = false,
  poolMaxTicketsByTicketTypeId,
}: TicketSelectionStepV2Props) {
  const getQuantity = (ticketTypeId: string): number => {
    const selection = selections.find((s) => s.ticketTypeId === ticketTypeId);
    return selection?.quantity ?? 0;
  };

  const getTotalSelectedTickets = (): number => {
    return selections.reduce((sum, s) => sum + s.quantity, 0);
  };

  const getMaxQuantityForTicket = (ticketType: V2TicketType): number => {
    const currentQuantity = getQuantity(ticketType.id);
    const guestsPerTicket = ticketType.numberOfGuestsPerTicket ?? 1;

    let ticketTypeAvailability = Infinity;
    if (
      ticketType.maxLimit !== undefined &&
      ticketType.currentSold !== undefined
    ) {
      if (guestsPerTicket > 0) {
        const availableSeats =
          ticketType.maxLimit * guestsPerTicket - ticketType.currentSold;
        ticketTypeAvailability = Math.floor(availableSeats / guestsPerTicket);
      } else {
        ticketTypeAvailability = Math.max(
          0,
          ticketType.maxLimit - ticketType.currentSold,
        );
      }
    }

    let sessionAvailability = Infinity;
    if (
      guestsPerTicket > 0 &&
      totalAvailability !== undefined &&
      totalAvailability > 0
    ) {
      if (ticketType.typeCharge === "flat") {
        const selectedFlatTickets = selections.reduce((sum, sel) => {
          const tt = ticketTypes.find((t) => t.id === sel.ticketTypeId);
          if (tt?.typeCharge === "flat") return sum + sel.quantity;
          return sum;
        }, 0);
        sessionAvailability =
          totalAvailability - selectedFlatTickets + currentQuantity;
      } else {
        const selectedSeats = selections.reduce((sum, sel) => {
          const tt = ticketTypes.find((t) => t.id === sel.ticketTypeId);
          const ttGuestsPerTicket = tt?.numberOfGuestsPerTicket ?? 1;
          return sum + sel.quantity * ttGuestsPerTicket;
        }, 0);
        const currentSeats = currentQuantity * guestsPerTicket;
        const availableSeats =
          totalAvailability - selectedSeats + currentSeats;
        sessionAvailability = Math.floor(availableSeats / guestsPerTicket);
      }
    }

    let bundleAvailability = Infinity;
    if (bundleMaxTickets !== undefined) {
      const totalSelectedTickets = getTotalSelectedTickets();
      bundleAvailability =
        bundleMaxTickets - totalSelectedTickets + currentQuantity;
    }

    // Equipment cap — per TICKET TYPE, deliberately OUTSIDE the
    // guestsPerTicket > 0 guards above: a zero-guest equipment add-on ticket
    // (affectsAvailability: false) bypasses every session-level cap and is
    // exactly the ticket a pool must still bound. Absent key = uncapped
    // (fail open).
    // Through the shared helper, not inline: this logic sat in the component
    // where nothing could execute it, and shipped reading a field the picker's
    // ticket objects never carried — so it was dead for its whole life.
    const poolCap = poolCapAcrossTicketTypes({
      ticketTypeId: ticketType.id,
      resourcePoolId: ticketType.resourcePoolId,
      resourceUnitsPerTicket: ticketType.resourceUnitsPerTicket,
      poolMaxTickets: poolMaxTicketsByTicketTypeId?.[ticketType.id],
      ticketTypes,
      selections,
    });

    let maxTickets = capSeatsByPool(
      Math.min(
        ticketType.maxQuantity,
        ticketTypeAvailability,
        sessionAvailability,
        bundleAvailability,
      ),
      poolCap,
    );

    if (maxTickets === Infinity) {
      if (guestsPerTicket > 0) {
        maxTickets = totalAvailability ?? ticketType.maxQuantity ?? 10;
      } else {
        maxTickets = ticketType.maxQuantity ?? 10;
      }
    }

    if (ticketType.typeCharge === "flat") {
      return Math.min(1, maxTickets);
    }

    return Math.max(0, maxTickets);
  };

  const getTicketErrors = (ticketTypeId: string): ValidationError[] => {
    return constraintErrors.filter((e) => e.ticketTypeId === ticketTypeId);
  };

  const isMultiDate = isMultiDateEnabled && dateCount > 1;
  const [isHelpOpen, setHelpOpen] = React.useState(false);

  return (
    <div className={cn("space-y-6", className)}>
      <CheckoutSteps.Header
        title="Tickets"
        description={
          isMultiDate
            ? "Select the number of people attending. The ticket will automatically apply to all dates."
            : "Select the number of tickets you'd like to purchase."
        }
      />

      {showBundleMembershipHelp && (
        <>
          <Button
            type="button"
            variant="outline"
            className="w-full justify-start gap-2"
            onClick={() => setHelpOpen(true)}
          >
            <Info className="h-4 w-4" aria-hidden="true" />
            Have a bundle or membership? Tap to learn more
          </Button>

          <BundleMembershipHelpPopup
            open={isHelpOpen}
            onOpenChange={setHelpOpen}
          />
        </>
      )}

      {cartPricingRules && cartPricingRules.length > 0 && (
        <DiscountHints rules={cartPricingRules} currency={currency} />
      )}

      <div className="space-y-3">
        <p className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
          Choose your tickets
        </p>

        <TicketSelectorV2.Group>
          {ticketTypes.map((ticketType) => {
            const maxQuantity = getMaxQuantityForTicket(ticketType);
            const isSoldOut =
              ticketType.soldOut ||
              ticketType.isInTimeConstraint === false ||
              maxQuantity === 0;

            const displayPrice =
              ticketType.effectivePrice ?? ticketType.price;
            const displayDepositAmount =
              ticketType.effectiveDepositAmount ?? ticketType.depositAmount;

            const effectiveBookingPolicy =
              ticketType.bookingPolicy ?? fallbackBookingPolicy ?? undefined;

            const ticketErrors = getTicketErrors(ticketType.id);

            // NB: `soldOut` is passed, `disabled` is NOT. They used to carry
            // the same expression, and `disabled` additionally blocks the
            // DECREMENT — which strands a quantity already held when a cap
            // tightens under it. `disabled` stays free for a genuine whole-row
            // disable; see canDecrease in ticket-selector-v2.
            return (
              <TicketSelectorV2
                key={ticketType.id}
                id={ticketType.id}
                name={ticketType.name}
                description={ticketType.description}
                price={hidePrice ? undefined : displayPrice}
                currency={currency}
                quantity={getQuantity(ticketType.id)}
                minQuantity={ticketType.minQuantity ?? 0}
                maxQuantity={maxQuantity}
                soldOut={isSoldOut}
                onChange={(qty) => onSelectionChange(ticketType.id, qty)}
                ticketTypeGroup={ticketType.ticketTypeGroup}
                depositAmount={displayDepositAmount}
                fullPrice={displayPrice}
                priceSuffix={isMultiDate ? "/ per date" : undefined}
                membershipRequired={ticketType.membershipRequired}
                membershipAccessGranted={ticketType.membershipAccessGranted}
                membershipsUrl={membershipsUrl}
                eligibilityMessage={ticketType.eligibilityMessage}
                policyHint={ticketType.policyHint}
                bookingPolicy={effectiveBookingPolicy}
                onPolicyClick={onPolicyClick}
                validationErrors={ticketErrors}
                pricingRules={ticketType.pricingRules}
                checkoutSummary={ticketType.checkoutSummary ?? null}
              />
            );
          })}
        </TicketSelectorV2.Group>
      </div>

    </div>
  );
}

// --- Discount Hints ---

type DiscountHint = {
  name: string;
  discount: string;
  conditions: string[];
};

function formatConditionLabel(c: any): string | null {
  switch (c.conditionType) {
    case "MIN_QUANTITY": return `Buy ${c.config.minQuantity}+ tickets`;
    case "MIN_SESSIONS": return `Book ${c.config.minSessions}+ sessions`;
    case "SESSION_WINDOW": return `Sessions within ${c.config.timeWindowDays} days`;
    case "BOOKING_ADVANCE": return `Book ${c.config.daysBeforeEvent}+ days early`;
    case "LAST_MINUTE": return `Book within ${c.config.hoursBeforeEvent}h of event`;
    case "MEMBER_TAGS": return `Members only`;
    case "RETURNING_CUSTOMER": return `Returning customers`;
    case "FIRST_TIME_BOOKING": return `First-time customers`;
    case "CONSECUTIVE_SESSIONS": return `${c.config.minConsecutive}+ consecutive weeks`;
    case "MONTH_RANGE": {
      const MS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
      return `${c.config.startDay ?? 1} ${MS[(c.config.startMonth ?? 1) - 1]} – ${c.config.endDay ?? 31} ${MS[(c.config.endMonth ?? 3) - 1]}`;
    }
    default: return null;
  }
}

function buildHints(rules: any[], currency: string): DiscountHint[] {
  return rules
    .filter((r: any) => r.ruleType === "CONDITIONAL_DISCOUNT" && r.status === "enabled")
    .map((r: any) => {
      // `null` for anything that doesn't lower the price — this panel is a
      // promo surface, and a dialog-authored surcharge is a CONDITIONAL_DISCOUNT
      // too.
      const discount = ruleDiscountHintLabel(r, currency);
      if (!discount) return null;
      // The SAME source the engine evaluates (`evaluateConditionGroups`:
      // groups first, legacy `conditions` as the fallback) — a dialog-authored
      // rule has its conditions only in `conditionGroups`, so reading
      // `conditions` alone showed no hint for any of them.
      const conditions = ruleEvaluatedConditions(r)
        .map(formatConditionLabel)
        .filter(Boolean) as string[];
      if (conditions.length === 0) return null;
      return { name: r.name, discount, conditions };
    })
    .filter((h): h is DiscountHint => h !== null);
}

function DiscountHints({ rules, currency }: { rules: any[]; currency: string }) {
  const [isExpanded, setIsExpanded] = React.useState(false);
  const hints = React.useMemo(() => buildHints(rules, currency), [rules, currency]);

  if (hints.length === 0) return null;

  const firstHint = hints[0];
  if (!firstHint) return null;

  const allConditions = hints.flatMap((h) =>
    h.conditions.map((c) => ({ condition: c, discount: h.discount, name: h.name })),
  );
  const visibleConditions = isExpanded ? allConditions : allConditions.slice(0, 2);
  const hiddenCount = allConditions.length - 2;

  return (
    <div className="flex items-start gap-2.5 rounded-lg border border-emerald-200 dark:border-emerald-900/40 bg-emerald-50/50 dark:bg-emerald-950/20 px-3 py-2.5">
      <Tag className="h-4 w-4 text-emerald-500 shrink-0 mt-0.5" />
      <div className="min-w-0">
        <span className="text-sm font-semibold text-emerald-700 dark:text-emerald-400">
          {firstHint.discount}
        </span>
        <div className="mt-1 space-y-0.5">
          {visibleConditions.map((item, i) => (
            <p key={i} className="text-xs text-muted-foreground leading-relaxed">{item.condition}</p>
          ))}
        </div>
        {hiddenCount > 0 && !isExpanded && (
          <button
            type="button"
            onClick={() => setIsExpanded(true)}
            className="mt-1 text-xs font-medium text-primary hover:text-primary/80 transition-colors"
          >
            +{hiddenCount} more
          </button>
        )}
        {isExpanded && hiddenCount > 0 && (
          <button
            type="button"
            onClick={() => setIsExpanded(false)}
            className="mt-1 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors"
          >
            Show less
          </button>
        )}
      </div>
    </div>
  );
}

export { TicketSelectionStepV2 };
export type { V2TicketType, TicketSelectionStepV2Props };
